import { useEffect, useMemo, useRef, useState } from 'react';
import { ScrollView } from 'react-native-gesture-handler';
import {
  UseQuery,
  UseQueryStateOptions,
} from '@reduxjs/toolkit/dist/query/react/buildHooks';
import lodashFilter from 'lodash/filter';
import lodashIsEmpty from 'lodash/isEmpty';
import lodashIsEqual from 'lodash/isEqual';

import constants from '../../Config/constants';
import messages from '../../Config/messages';
import { numberToArray } from '../../Helper';
import UrlParameter from '../../Helper/UrlParameter';
import { LimitAndOffset, PaginatedResult } from '../../RTK/api/types';
import Service from '../../Service';

const getError = (errResponse: any) => {
  if (lodashIsEmpty(errResponse)) return '';
  return (
    Service.handleErrorMessage(errResponse?.data?.message) ||
    messages.COMMON_ERROR_MESSAGE
  );
};

type InfiniteScrollReturnType<ItemType> = {
  data: ItemType[];
  error: string;
  hasNext: boolean;
  isLoading: boolean;
  isLoadingMore: boolean;
  listProps: {
    manual: boolean;
    [key: string]: any;
  };
  page: number;
  refreshing: boolean;
  scrollRef: any;
  total: number;
  getData: (filter: any | object) => ItemType[];
  onMomentumScrollBegin: () => void;
  onReachedEnd: () => void;
  onRefresh: () => void;
  // web specific function
  isCloseToBottom: (event: any) => void;
  setScrollableContentHeight: (width: number, height: number) => void;
};

const useInfiniteScrollController = <ItemType>({
  loaderCount = 1,
  manual = false,
  queryOptions,
  queryParams,
  onLoad,
  useQuery,
  ...rest
}: ControllerProps): InfiniteScrollReturnType<ItemType> => {
  const loader = numberToArray(loaderCount).map(() => ({
    loading: true,
  })) as ItemType[];

  const isEndReachedCalledDuringMomentum = useRef<boolean>(true);
  const isFirstQueryTriggered = useRef<boolean>(false);
  const memoizedParams = useMemo(() => queryParams, [queryParams]);
  const prevParamsRef = useRef<string>(JSON.stringify(queryParams));

  const [internalPage, setInternalPage] = useState<number>(1);
  const [listData, setListData] = useState<ItemType[]>(loader);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [isLoadingMore, setLoadingMore] = useState<boolean>(false);

  const limitAndOffset = UrlParameter.getLimitAndOffset(
    internalPage,
    true,
  ) as LimitAndOffset;
  const query = useQuery(
    { payload: { ...limitAndOffset, ...memoizedParams } },
    queryOptions,
  );

  const {
    has_next_page = false,
    page = 0,
    result = [],
    total_records = 0,
  } = (query.data || {}) as PaginatedResult;

  const errorMsg = getError(query.error);
  const list = lodashIsEmpty(query.error) ? listData : [];
  const isLoading = query.isLoading;
  const hasNext = Boolean(has_next_page);

  useEffect(() => {
    const currentParams = JSON.stringify(memoizedParams);
    const prevParams = prevParamsRef.current;
    if (isFirstQueryTriggered.current) {
      if (
        !lodashIsEqual(prevParams, currentParams) && // if queryParams changed
        !query.isSuccess // make sure its not success yet to not reset the cache data and reset it will loader
      ) {
        _resetState(); // reset state, since new query
      }
    }
    prevParamsRef.current = currentParams; // update previous params
  }, [memoizedParams, queryOptions?.skip]);

  useEffect(() => {
    if (query.isSuccess && internalPage === page) {
      if (page === 1) {
        if (!isFirstQueryTriggered.current) {
          isFirstQueryTriggered.current = true;
          onLoad?.();
        }
        setListData(result as ItemType[]);
      } else {
        setListData((prev) => [...prev, ...result] as ItemType[]);
      }
      setLoadingMore(false);
      if (refreshing) setRefreshing(false);
    }
  }, [result]);

  // Return the infinite scroll data
  const _getData = (filter?: () => any | object) => {
    if (lodashIsEmpty(result)) return [] as ItemType[];
    const isFunc = typeof filter === 'function';
    const isObj =
      typeof filter === 'object' && !Array.isArray(filter) && filter !== null;
    if (isFunc || isObj) {
      return lodashFilter(result, filter) as ItemType[];
    } else {
      return result as ItemType[];
    }
  };

  const _loadNext = () => {
    isEndReachedCalledDuringMomentum.current = true;
    setLoadingMore(true);
    setInternalPage((prev) => prev + 1);
  };

  const _onReachedEnd = () => {
    if (!manual && hasNext) {
      if (!isEndReachedCalledDuringMomentum.current || constants.isWeb) {
        _loadNext();
      }
    }
  };

  const _onMomentumScrollBegin = () => {
    isEndReachedCalledDuringMomentum.current = false;
  };

  const _onRefresh = () => {
    if (query.status !== 'uninitialized') {
      if (!query.isFetching && !refreshing) {
        if (internalPage === 1) {
          query.refetch(true);
        } else {
          setRefreshing(true);
          _resetState();
        }
      }
    }
  };

  const _resetState = () => {
    setInternalPage(1);
    setListData(loader);
  };

  // #region Web Specific
  const scrollableContentHeight = useRef<number | undefined>();
  const scrollRef = useRef<ScrollView | undefined>();
  const isCanAutoNext = hasNext && !manual && !isLoading; // has next, not manual and not loading
  if (constants.isWeb) {
    // this hooks handle if the scrollview is not scrollable but hasNext it automatically fetch next until the scrollview become scrollable
    useEffect(() => {
      if (!isLoading && hasNext && scrollableContentHeight.current) {
        // @ts-ignore: measure not recognize on react native, but it works on web
        scrollRef.current.measure(
          (_x: number, _y: number, _width: number, height: number) => {
            const scrollable = scrollableContentHeight.current > height;
            if (!scrollable && isCanAutoNext) {
              _loadNext();
            }
          },
        );
      }
    }, [isLoading, hasNext]);
  }

  const _isCloseToBottom = (event: any) => {
    const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
    const isNearBottom =
      layoutMeasurement.height + contentOffset.y >= contentSize.height - 20;
    if (isNearBottom && isCanAutoNext) {
      _loadNext();
    }
  };

  const _setScrollableContentHeight = (
    _contentWidth: number,
    contentHeight: number,
  ) => {
    scrollableContentHeight.current = contentHeight;
  };
  // #endregion

  return {
    data: list,
    error: errorMsg,
    hasNext,
    isLoading,
    isLoadingMore,
    listProps: { manual, ...rest },
    page,
    refreshing,
    scrollRef, // for web
    total: total_records,
    getData: _getData,
    onMomentumScrollBegin: _onMomentumScrollBegin,
    onReachedEnd: _onReachedEnd,
    onRefresh: _onRefresh,
    // web specific function
    isCloseToBottom: _isCloseToBottom,
    setScrollableContentHeight: _setScrollableContentHeight,
  };
};

export type ControllerProps = {
  loaderCount?: number;
  manual?: boolean;
  queryOptions?: UseQueryStateOptions<any, any>;
  queryParams?: object;
  onLoad?: Function;
  useQuery: UseQuery<any>;
};

export default useInfiniteScrollController;
